import { TranslateService } from '@ngx-translate/core';
import {
  InvoiceAttentionReason,
  InvoiceImportChangeSeverity,
  InvoiceImportCountSource,
  InvoiceImportGoodsReceiptStatus,
  InvoiceImportInfoReason,
  InvoiceImportLine,
  InvoiceImportPairingStatus,
  InvoiceImportStatus,
} from '../models/invoice-import.model';

export interface InvoiceImportDisplayRow {
  line: InvoiceImportLine;
  indentLevel: number;
  isDepositChild: boolean;
  hideApproveReject: boolean;
}

const RETAIL_INFO_ATTENTION_LEGACY: InvoiceImportInfoReason[] = ['retail_above_price', 'retail_below_price'];

const DKK = new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' });
const DKK_PLAIN = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatDkk(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return DKK.format(value);
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits).replace('.', ',')} %`;
}

/** Plain kr amount with Danish grouping (no currency symbol). */
export function formatKrPlain(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return DKK_PLAIN.format(value);
}

export function formatSignedKrDelta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatKrPlain(Math.abs(value))} kr`;
}

export function changeSeverityRank(severity: InvoiceImportChangeSeverity | undefined): number {
  switch (severity) {
    case 'large':
      return 5;
    case 'notice':
      return 4;
    case 'normal':
      return 3;
    case 'small':
      return 2;
    case 'none':
      return 1;
    default:
      return 0;
  }
}

export function isUnmatchedProductLine(line: InvoiceImportLine): boolean {
  return line.lineType === 'product' && (line.matchStatus === 'unmatched' || line.matchStatus === 'ambiguous');
}

export function isLargeReviewLine(line: InvoiceImportLine): boolean {
  return line.reviewStatus === 'pending' && line.changeSeverity === 'large';
}

export function isToCheckReviewLine(line: InvoiceImportLine): boolean {
  if (line.reviewStatus !== 'pending') {
    return false;
  }
  if (line.changeSeverity === 'large') {
    return false;
  }
  return !!(line.needsAttention || line.matchStatus === 'unmatched' || line.matchStatus === 'ambiguous');
}

export function isUnchangedPendingLine(line: InvoiceImportLine): boolean {
  return line.reviewStatus === 'pending' && line.matchStatus === 'matched' && !line.needsAttention;
}

export function effectiveTotalUnits(line: InvoiceImportLine): number {
  if (line.totalUnits != null && !Number.isNaN(line.totalUnits)) {
    return line.totalUnits;
  }
  if (line.orderedPacks != null && line.quantity != null) {
    return line.orderedPacks * line.quantity;
  }
  return line.quantity ?? 0;
}

export function mergeUpdatedLine(lines: InvoiceImportLine[], updated: InvoiceImportLine): InvoiceImportLine[] {
  const idx = lines.findIndex((l) => l._id === updated._id);
  if (idx >= 0) {
    const next = [...lines];
    next[idx] = updated;
    return next;
  }
  return [...lines, updated];
}

export function lineDraftFromLine(line: InvoiceImportLine): {
  acceptCostPrice: boolean;
  newSalePrice: number | null;
  costKeepReason: string;
} {
  return {
    acceptCostPrice: line.acceptCostPrice !== false,
    newSalePrice: line.newSalePrice ?? null,
    costKeepReason: line.costKeepReason || '',
  };
}

export function shouldShowInvoiceGroupHeader(
  line: { invoiceNumber?: string },
  index: number,
  lines: { invoiceNumber?: string }[]
): boolean {
  if (index === 0) {
    return true;
  }
  const prev = lines[index - 1];
  return (prev.invoiceNumber || '') !== (line.invoiceNumber || '');
}

export function sortInvoiceImportReviewLines(lines: InvoiceImportLine[]): InvoiceImportLine[] {
  return [...lines].sort((a, b) => {
    const rankA = changeSeverityRank(a.changeSeverity);
    const rankB = changeSeverityRank(b.changeSeverity);
    if (rankA !== rankB) {
      return rankB - rankA;
    }
    if (rankA >= changeSeverityRank('large')) {
      const umA = isUnmatchedProductLine(a);
      const umB = isUnmatchedProductLine(b);
      if (umA !== umB) {
        return umA ? 1 : -1;
      }
    }
    const scoreA = a.changeScore ?? 0;
    const scoreB = b.changeScore ?? 0;
    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }
    return (a.lineIndex ?? 0) - (b.lineIndex ?? 0);
  });
}

export function visibleAttentionReasons(reasons: InvoiceAttentionReason[] | undefined): InvoiceAttentionReason[] {
  if (!reasons?.length) {
    return [];
  }
  return reasons.filter(
    (r) => r !== 'invoice_mismatch' && !RETAIL_INFO_ATTENTION_LEGACY.includes(r as InvoiceImportInfoReason)
  );
}

/** §10.3 infoReasons plus legacy retail_* still stored on attentionReasons. */
export function resolveLineInfoReasons(line: {
  infoReasons?: InvoiceImportInfoReason[];
  attentionReasons?: InvoiceAttentionReason[];
}): InvoiceImportInfoReason[] {
  const merged: InvoiceImportInfoReason[] = [...(line.infoReasons || [])];
  for (const r of line.attentionReasons || []) {
    if (RETAIL_INFO_ATTENTION_LEGACY.includes(r as InvoiceImportInfoReason) && !merged.includes(r as InvoiceImportInfoReason)) {
      merged.push(r as InvoiceImportInfoReason);
    }
  }
  return merged;
}

export function infoReasonKey(reason: InvoiceImportInfoReason): string {
  return `INVOICE_IMPORTS.ATTENTION.${reason.toUpperCase()}`;
}

export function buildInvoiceImportDisplayRows(
  productLines: InvoiceImportLine[],
  allLines: InvoiceImportLine[],
  includeTransportFees: boolean
): InvoiceImportDisplayRow[] {
  const rows: InvoiceImportDisplayRow[] = [];
  const depositsByParent = new Map<string, InvoiceImportLine[]>();
  for (const line of allLines) {
    if (line.lineType === 'deposit' && line.parentLineId) {
      const list = depositsByParent.get(line.parentLineId) || [];
      list.push(line);
      depositsByParent.set(line.parentLineId, list);
    }
  }
  for (const parent of productLines) {
    rows.push({
      line: parent,
      indentLevel: 0,
      isDepositChild: false,
      hideApproveReject: false,
    });
    const children = depositsByParent.get(parent._id) || [];
    for (const child of children.sort((a, b) => (a.lineIndex ?? 0) - (b.lineIndex ?? 0))) {
      rows.push({
        line: child,
        indentLevel: 1,
        isDepositChild: true,
        hideApproveReject: true,
      });
    }
  }
  if (includeTransportFees) {
    for (const fee of allLines) {
      if (fee.lineType === 'fee' && fee.transportStock) {
        rows.push({
          line: fee,
          indentLevel: 0,
          isDepositChild: false,
          hideApproveReject: true,
        });
      }
    }
  }
  return rows;
}

export function isDraftLinkedLine(line: InvoiceImportLine): boolean {
  return !!(line.draftProductId || line.matchMethod === 'auto_created');
}

export function hasQtyPriceSuspect(line: InvoiceImportLine): boolean {
  return (line.attentionReasons || []).includes('qty_price_suspect');
}

export function countDeviation(line: InvoiceImportLine): number | null {
  if (line.countedUnits == null || line.totalUnits == null) {
    return null;
  }
  return line.countedUnits - line.totalUnits;
}

export function countSourceKey(source: InvoiceImportCountSource | null | undefined): string {
  if (!source) {
    return '';
  }
  return `INVOICE_IMPORTS.PHASE2.COUNT_SOURCE.${source.toUpperCase()}`;
}

export function goodsReceiptStatusKey(status: InvoiceImportGoodsReceiptStatus | undefined): string {
  const s = status || 'none';
  return `INVOICE_IMPORTS.PHASE2.GOODS_RECEIPT.${s.toUpperCase()}`;
}

export function pairingStatusKey(status: InvoiceImportPairingStatus | undefined): string {
  const s = status || 'none';
  return `INVOICE_IMPORTS.PHASE2.PAIRING.${s.toUpperCase()}`;
}

export function draftSkipReasonKey(code: string): string {
  return `INVOICE_IMPORTS.PHASE2.DRAFT_SKIP.${code.toUpperCase()}`;
}

export function costLayerSourceKey(source: string | undefined): string {
  if (!source) {
    return '';
  }
  return `INVOICE_IMPORTS.PHASE2.COST_LAYER_SOURCE.${source.toUpperCase()}`;
}

export function invoiceImportStatusBadgeClass(status: InvoiceImportStatus): string {
  switch (status) {
    case 'paired':
      return 'bg-secondary';
    case 'ready_for_review':
      return 'bg-primary';
    case 'partially_applied':
      return 'bg-info';
    case 'applied':
      return 'bg-success';
    case 'parse_failed':
    case 'rejected_sender':
      return 'bg-danger';
    case 'duplicate':
      return 'bg-secondary';
    case 'needs_store':
      return 'bg-warning text-dark';
    case 'parsing':
    case 'pending_parse':
      return 'bg-light text-dark';
    case 'archived':
      return 'bg-dark';
    default:
      return 'bg-secondary';
  }
}

export function attentionReasonKey(reason: InvoiceAttentionReason): string {
  return `INVOICE_IMPORTS.ATTENTION.${reason.toUpperCase()}`;
}

export function attentionReasonParams(
  reason: InvoiceAttentionReason,
  line: { costDeltaPercent?: number | null }
): Record<string, string | number> {
  if (reason === 'cost_increase' || reason === 'cost_decrease') {
    return { percent: line.costDeltaPercent != null ? Math.abs(line.costDeltaPercent).toFixed(1) : '?' };
  }
  return {};
}

export function applyBlockedReasonKey(code: string): string {
  return `INVOICE_IMPORTS.APPLY_BLOCKED.${code.toUpperCase()}`;
}

export function stockSkippedReasonKey(code: string | undefined): string {
  if (!code) {
    return '';
  }
  return `INVOICE_IMPORTS.STOCK_SKIPPED.${code.toUpperCase()}`;
}

/** Machine-readable API error code (§10.5 envelope). */
export function apiErrorCode(err: unknown): string | undefined {
  const e = err as { error?: { response?: { data?: { code?: string }; code?: string }; code?: string } };
  return e?.error?.response?.data?.code ?? e?.error?.response?.code ?? e?.error?.code;
}

/** i18n key for substitution API error codes (§13.5), or undefined if unmapped. */
export function substitutionErrorKey(code: string | undefined): string | undefined {
  if (!code) {
    return undefined;
  }
  const map: Record<string, string> = {
    SUB_ADVICE_ROW_NOT_FOUND: 'INVOICE_IMPORTS.SUB.ERR_ROW_NOT_FOUND',
    SUBSTITUTION_CONFIRM_REQUIRED: 'INVOICE_IMPORTS.SUB.ERR_CONFIRM_REQUIRED',
    SUBSTITUTION_FROM_REQUIRED: 'INVOICE_IMPORTS.SUB.ERR_FROM_REQUIRED',
    SUBSTITUTION_SCOPE_REQUIRED: 'INVOICE_IMPORTS.SUB.ERR_SCOPE_REQUIRED',
    SUBSTITUTION_EXISTS: 'INVOICE_IMPORTS.SUB.EXISTS_BODY',
  };
  return map[code];
}

export function substitutionErrorMessage(translate: TranslateService, err: unknown): string {
  const code = apiErrorCode(err);
  const key = substitutionErrorKey(code);
  if (key) {
    return translate.instant(key);
  }
  const e = err as { error?: { response?: { message?: string } }; message?: string };
  return e?.error?.response?.message || e?.message || 'Error';
}

export function isInvoiceImportPdfFile(file: { mimeType?: string; filename?: string }): boolean {
  const mime = (file.mimeType || '').toLowerCase();
  if (mime === 'application/pdf') {
    return true;
  }
  return (file.filename || '').toLowerCase().endsWith('.pdf');
}

export function translateInstant(translate: TranslateService, key: string, params?: Record<string, unknown>): string {
  const v = translate.instant(key, params);
  return v === key ? key : v;
}

export function enumLabelKey(prefix: string, value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return `INVOICE_IMPORTS.${prefix}.${value.toUpperCase()}`;
}

export function reviewStatusKey(status: string): string {
  return enumLabelKey('REVIEW_STATUS', status);
}

export function lineTypeKey(lineType: string): string {
  return enumLabelKey('LINE_TYPE', lineType);
}

export function matchStatusKey(matchStatus: string): string {
  return enumLabelKey('MATCH_STATUS', matchStatus);
}

export function storeResolutionKey(resolution: string): string {
  return enumLabelKey('STORE_RESOLUTION', resolution);
}

export function resolutionNoteKey(note: string): string {
  return enumLabelKey('RESOLUTION_NOTE', note);
}

export const INVOICE_IMPORT_MAX_PDF_BYTES = 15 * 1024 * 1024;
export const INVOICE_IMPORT_MAX_FILES = 10;

function isAllowedInvoiceUploadFile(f: File): boolean {
  const name = f.name.toLowerCase();
  return (
    f.type === 'application/pdf' ||
    name.endsWith('.pdf') ||
    name.endsWith('.fak') ||
    f.type === 'application/zip' ||
    f.type === 'application/x-zip-compressed' ||
    name.endsWith('.zip')
  );
}

export function validateInvoiceUploadFiles(files: FileList | File[]): { valid: File[]; errors: string[] } {
  const list = Array.from(files);
  const errors: string[] = [];
  if (list.length > INVOICE_IMPORT_MAX_FILES) {
    errors.push('too_many');
    return { valid: [], errors };
  }
  const valid: File[] = [];
  for (const f of list) {
    if (!isAllowedInvoiceUploadFile(f)) {
      errors.push('invalid_type');
      continue;
    }
    if (f.size > INVOICE_IMPORT_MAX_PDF_BYTES) {
      errors.push('too_large');
      continue;
    }
    valid.push(f);
  }
  return { valid, errors };
}

/** @deprecated use validateInvoiceUploadFiles */
export function validatePdfFiles(files: FileList | File[]): { valid: File[]; errors: string[] } {
  return validateInvoiceUploadFiles(files);
}
